import React from 'react';
import PropTypes from 'prop-types';

import { Badge, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';

import ActionsMenu from 'components/shared/ActionsMenu';
import L4CategorySortField from 'lib/enums/L4CategorySortField';
import SortDirection from 'lib/enums/SortDirection';
import {
  backgroundGrey,
  backgroundGrey1,
  blackberry,
  grape,
  positiveGreen,
  veryLightGray,
  white,
} from 'lib/css/colors';

function LargeCategoriesTable({
  categories,
  sendingToML,
  sentCategories,
  onSendToML,
  onExportProducts,
  exportingCategoryId,
  sortBy,
  sortOrder,
  onSort,
}) {
  const renderSortIcon = field => {
    if (sortBy === field) {
      return (
        <FontAwesomeIcon
          icon={sortOrder === SortDirection.DESC ? faSortDown : faSortUp}
          className={css(styles.sortIconActive)}
        />
      );
    }
    return <FontAwesomeIcon icon={faSort} className={css(styles.sortIconInactive)} />;
  };

  return (
    <Table hover className={css(styles.table)}>
      <thead className={css(styles.tableHead)}>
        <tr>
          <th style={{ width: '10%' }}>L0</th>
          <th style={{ width: '12%' }}>L1</th>
          <th style={{ width: '14%' }}>L2</th>
          <th style={{ width: '14%' }}>L3</th>
          <th style={{ width: '18%' }}>L4</th>
          <th
            style={{ width: '8%' }}
            className={`text-center ${css(styles.sortableHeader)}`}
            onClick={() => onSort(L4CategorySortField.PRODUCT_COUNT)}
          >
            Products {renderSortIcon(L4CategorySortField.PRODUCT_COUNT)}
          </th>
          <th
            style={{ width: '10%' }}
            className={`text-center ${css(styles.sortableHeader)}`}
            onClick={() => onSort(L4CategorySortField.L90D_GMV)}
          >
            L90d GMV {renderSortIcon(L4CategorySortField.L90D_GMV)}
          </th>
          <th style={{ width: '14%' }} className="text-center">
            Action
          </th>
        </tr>
      </thead>
      <tbody>
        {categories.map(category => {
          const isSent = sentCategories.has(category.l4_category_id);

          return (
            <tr key={category.l4_category_id} className={css(styles.tableRow)}>
              <td className={css(styles.cell)}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-l0-${category.l4_category_id}`}>
                      {category.l0_category_name}
                    </Tooltip>
                  }
                >
                  <span className={css(styles.cellText)}>{category.l0_category_name}</span>
                </OverlayTrigger>
              </td>
              <td className={css(styles.cell)}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-l1-${category.l4_category_id}`}>
                      {category.l1_category_name}
                    </Tooltip>
                  }
                >
                  <span className={css(styles.cellText)}>{category.l1_category_name}</span>
                </OverlayTrigger>
              </td>
              <td className={css(styles.cell)}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-l2-${category.l4_category_id}`}>
                      {category.l2_category_name}
                    </Tooltip>
                  }
                >
                  <span className={css(styles.cellText)}>{category.l2_category_name}</span>
                </OverlayTrigger>
              </td>
              <td className={css(styles.cell)}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-l3-${category.l4_category_id}`}>
                      {category.l3_category_name}
                    </Tooltip>
                  }
                >
                  <span className={css(styles.cellText)}>{category.l3_category_name}</span>
                </OverlayTrigger>
              </td>
              <td className={css(styles.cell)}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-l4-${category.l4_category_id}`}>
                      {category.l4_category_name}
                    </Tooltip>
                  }
                >
                  <span className={css(styles.l4Name)}>{category.l4_category_name}</span>
                </OverlayTrigger>
              </td>
              <td className="text-center">
                <Badge pill className={css(styles.countBadge)}>
                  {category.product_count.toLocaleString()}
                </Badge>
              </td>
              <td className="text-center">
                <span className={css(styles.gmvText)}>
                  {category.l90d_gmv != null
                    ? `$${category.l90d_gmv.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}`
                    : '-'}
                </span>
              </td>
              <td className="text-center">
                <div className={css(styles.actionCell)}>
                  {isSent && (
                    <Badge className={css(styles.sentBadge)}>
                      <FontAwesomeIcon icon={faPaperPlane} className="mr-1" />
                      Sent
                    </Badge>
                  )}
                  <ActionsMenu
                    disabled={sendingToML}
                    actions={[
                      {
                        label: 'Send for AI Segmentation',
                        onClick: () => onSendToML(category.l4_category_id),
                        disabled: isSent || sendingToML,
                        hidden: isSent,
                      },
                      {
                        label: 'Export products',
                        onClick: () => onExportProducts(category.l4_category_id),
                        disabled: exportingCategoryId === category.l4_category_id,
                      },
                    ]}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

const styles = StyleSheet.create({
  table: {
    marginBottom: 0,
  },
  tableHead: {
    backgroundColor: backgroundGrey,
  },
  tableRow: {
    ':hover': {
      backgroundColor: backgroundGrey1,
    },
  },
  cell: {
    verticalAlign: 'middle',
    padding: '12px 8px',
  },
  cellText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '13px',
  },
  l4Name: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    fontWeight: 600,
    color: grape,
  },
  countBadge: {
    fontSize: '12px',
    padding: '6px 12px',
    backgroundColor: blackberry,
    color: white,
  },
  gmvText: {
    fontSize: '13px',
    fontWeight: 500,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    ':hover': {
      backgroundColor: veryLightGray,
    },
  },
  sortIconActive: {
    marginLeft: '4px',
    fontSize: '12px',
    color: grape,
  },
  sortIconInactive: {
    marginLeft: '4px',
    fontSize: '12px',
    opacity: 0.4,
  },
  actionCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  sentBadge: {
    fontSize: '12px',
    padding: '6px 12px',
    backgroundColor: positiveGreen,
    color: white,
  },
});

LargeCategoriesTable.propTypes = {
  categories: PropTypes.array.isRequired,
  sendingToML: PropTypes.bool,
  sentCategories: PropTypes.instanceOf(Set).isRequired,
  onSendToML: PropTypes.func.isRequired,
  onExportProducts: PropTypes.func.isRequired,
  exportingCategoryId: PropTypes.number,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  onSort: PropTypes.func,
};

export default LargeCategoriesTable;
